
/**
 * @typedef {Object} RechargeProduct
 * @property {Array<{id: string|number}>} variants
 * @property {Array<{id: string|number, external_plan_id: string|number}>} plans
 * @property {string|number} id
 */

/**
 * @typedef {Object} RechargeEvents
 * @property {(callback: (detail: string|number) => void) => void} onSellingPlanChange
 * @property {(callback: (detail: string|number) => void) => void} onVariantChange
 */

/**
 * @typedef {Object} RechargeWidgetData
 * @property {RechargeProduct} product
 * @property {string|number} variantId
 * @property {string|number} [sellingPlanId]
 * @property {RechargeEvents} events
 */

/**
 * Augment window with Recharge widget callback
 * @typedef {Window & {RechargeSubscriptionWidgetReady: (widgetData: RechargeWidgetData) => void}} WindowWithRecharge
 */

/**
 * Handles recharge selling plan changes and emits variant update events
 */
class RechargePrice {
  /** @type {RechargeWidgetData | null} */
  #rechargeData = null;

  /** @type {string | number | null} */
  #currentSellingPlanId = null;

  constructor() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.#init.bind(this));
    } else {
      this.#init();
    }
  }

  /**
   * Initializes the recharge price handler
   */
  #init() {
    /** @param {RechargeWidgetData} widgetData */
    const onRechargeWidgetReady = (widgetData) => {
      this.#rechargeData = widgetData;

      // Match the external id of the selected plan to the recharge id
      const planId = widgetData.sellingPlanId
        ? widgetData.product.plans.find((p) => p.external_plan_id === widgetData.sellingPlanId)?.id ?? null
        : null;
      this.#currentSellingPlanId = planId;

      if (widgetData.events) {
        widgetData.events.onSellingPlanChange((detail) => {
          const newPlanId = detail
            ? widgetData.product.plans.find((p) => p.external_plan_id === detail)?.id ?? null
            : null;
          this.#currentSellingPlanId = newPlanId;

          this.#emitSellingPlanChange(widgetData.variantId, this.#currentSellingPlanId);
        });

        widgetData.events.onVariantChange((detail) => {
          this.#emitSellingPlanChange(detail, this.#currentSellingPlanId);
        });
      }
    };

    // Assign to window with proper type casting
    window.RechargeSubscriptionWidgetReady = onRechargeWidgetReady;
  }

  /**
   * Emits a custom event when selling plan changes
   * @param {string|number} variantId - The variant ID
   * @param {string|number|null} sellingPlanId - The selling plan ID
   */
  #emitSellingPlanChange(variantId, sellingPlanId) {
    if (!variantId) return;

    // Emit event so variant-content can trigger a section re-render
    document.dispatchEvent(
      new CustomEvent('recharge:sellingPlanChange', {
        bubbles: true,
        detail: {
          variantId,
          sellingPlanId,
        },
      })
    );
  }
}

export const rechargePrice = new RechargePrice();
